import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, CheckCircle, XCircle } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";

function RuleForm({ rule, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: rule?.name || "", domain_pattern: rule?.domain_pattern || "",
    param_key: rule?.param_key || "", param_value: rule?.param_value || "",
    url_prefix: rule?.url_prefix || "", is_active: rule?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [testUrl, setTestUrl] = useState("");
  const [resolvedUrl, setResolvedUrl] = useState("");

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (rule?.id) await api.put(`/affiliate/${rule.id}`, form);
      else await api.post("/affiliate/", form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    try {
      const res = await api.post("/affiliate/resolve", { url: testUrl });
      setResolvedUrl(res.data.resolved);
    } catch { setResolvedUrl("Fehler"); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-zinc-900 border border-white/5 rounded-xl p-5">
      {error && <div className="text-red-400 text-sm">{error}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Name</label>
          <input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} required className={inputCls} placeholder="z.B. Amazon DE" />
        </div>
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Domain Pattern</label>
          <input value={form.domain_pattern} onChange={(e) => setForm(f => ({...f, domain_pattern: e.target.value}))} required className={inputCls} placeholder="z.B. amazon.de" />
        </div>
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Parameter Key</label>
          <input value={form.param_key} onChange={(e) => setForm(f => ({...f, param_key: e.target.value}))} required className={inputCls} placeholder="z.B. tag" />
        </div>
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Parameter Value</label>
          <input value={form.param_value} onChange={(e) => setForm(f => ({...f, param_value: e.target.value}))} className={inputCls} placeholder="z.B. huwnd-21" />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({...f, is_active: e.target.checked}))} className="w-4 h-4 accent-rose-600" />
        <span className="text-zinc-300 text-sm">Aktiv</span>
      </label>
      {/* Test field */}
      <div className="border-t border-white/5 pt-4">
        <label className="text-zinc-500 text-xs block mb-1.5">Link testen</label>
        <div className="flex gap-2">
          <input value={testUrl} onChange={(e) => setTestUrl(e.target.value)} className={`${inputCls} flex-1`} placeholder="https://..." />
          <button type="button" onClick={handleTest} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded-md">Testen</button>
        </div>
        {resolvedUrl && <p className="text-xs text-green-400 mt-1 break-all">{resolvedUrl}</p>}
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold text-sm rounded-md transition-colors">
          {saving ? "Speichern..." : "Speichern"}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md transition-colors">Abbrechen</button>
      </div>
    </form>
  );
}

export default function AffiliateAdminPage() {
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchRules = async () => {
    setLoading(true);
    try { const res = await api.get("/affiliate/"); setRules(res.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchRules(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Regel wirklich löschen?")) return;
    try { await api.delete(`/affiliate/${id}`); fetchRules(); }
    catch { alert("Fehler beim Löschen"); }
  };

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-white font-heading">Affiliate-Regeln</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Nur für Developer sichtbar</p>
        </div>
        <button onClick={() => { setCreating(true); setEditing(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-md transition-colors">
          <Plus size={16} /> Neue Regel
        </button>
      </div>

      {creating && (
        <div className="mb-6">
          <RuleForm onSave={() => { setCreating(false); fetchRules(); }} onCancel={() => setCreating(false)} />
        </div>
      )}
      {editing && (
        <div className="mb-6">
          <RuleForm rule={editing} onSave={() => { setEditing(null); fetchRules(); }} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full admin-table">
          <thead>
            <tr className="border-b border-white/5">
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left hidden sm:table-cell">Domain</th>
              <th className="px-4 py-3 text-left hidden md:table-cell">Parameter</th>
              <th className="px-4 py-3 text-left hidden lg:table-cell">Wert</th>
              <th className="px-4 py-3 text-left">Aktiv</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-4 text-center text-zinc-500 text-sm">Laden...</td></tr>
            ) : rules.map((r) => (
              <tr key={r.id} className="hover:bg-zinc-800/30 transition-colors">
                <td className="px-4 py-3 text-white text-sm">{r.name}</td>
                <td className="px-4 py-3 hidden sm:table-cell text-zinc-400 text-xs">{r.domain_pattern}</td>
                <td className="px-4 py-3 hidden md:table-cell text-zinc-400 text-xs font-mono">{r.param_key}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-zinc-400 text-xs font-mono">{r.param_value || "–"}</td>
                <td className="px-4 py-3">
                  {r.is_active
                    ? <CheckCircle size={16} className="text-green-500" />
                    : <XCircle size={16} className="text-zinc-600" />
                  }
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => { setEditing(r); setCreating(false); }}
                      className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors">
                      <Edit size={15} />
                    </button>
                    <button onClick={() => handleDelete(r.id)}
                      className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </AdminLayout>
  );
}
